/**
 * Nominee Cache Utility
 *
 * Caches nominee list in sessionStorage to avoid redundant API calls.
 * Cache is invalidated when nominees are added/removed or on logout.
 */

const CACHE_KEY = '__nominee_cache';
const CACHE_TIMESTAMP_KEY = '__nominee_cache_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached nominee data
 * Returns null if cache is invalid or expired
 */
export const getCachedNominees = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (!cached || !timestamp) {
      return null;
    }

    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_DURATION) {
      // Cache expired
      clearNomineeCache();
      return null;
    }

    return JSON.parse(cached);
  } catch (error) {
    return null;
  }
};

/**
 * Set nominee cache
 */
export const setCachedNominees = (nominees) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(nominees));
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    // Silently fail
  }
};

/**
 * Clear nominee cache
 * Call this when nominees are added, removed, or updated
 */
export const clearNomineeCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch (error) {
    // Silently fail
  }
};

/**
 * Check if cache exists and is valid
 */
export const hasCachedNominees = () => {
  const cached = getCachedNominees();
  return cached !== null;
};
